/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DayChip } from '../day-chip-list/day-chip.model';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import * as strings from '@touchplan/strings';
import { CalendarWorkdays, GroupedTicket } from '@touchplan/ticket';
import { map, takeUntil } from 'rxjs/operators';
import { DateUtils } from '../../../../utils/date-utils';
import { WorkDayUtils } from '../../../../utils/work-day-utils';
import { AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
	selector: 'tp-work-schedule',
	templateUrl: './work-schedule.component.html',
	styleUrls: ['./work-schedule.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: WorkScheduleComponent,
		},
	],
})
export class WorkScheduleComponent implements ControlValueAccessor, OnInit, OnDestroy {
	@Input({ required: true }) public liveStart$!: Observable<string | undefined>;
	@Input({ required: true }) public calendarWorkdays!: CalendarWorkdays;
	@Input({ required: true }) public editTicket$!: Observable<GroupedTicket>;

	public ticketWorkdaysOn$ = new BehaviorSubject<boolean[]>([]);

	public scheduleChips$ = new BehaviorSubject<DayChip[]>([]);

	public appStrings = strings;

	public selectedChipId: string | undefined = undefined;
	private shutdownSubject$ = new Subject<void>();
	private workingDaysSubscription: Subscription | undefined = undefined;

	// for ControlValueAccessor
	public isDisabled = false;
	public onTouched = () => {};
	private onValueChange = (_: boolean[]) => {};

	constructor() {}

	@Input({ required: true }) public set workingDays(workingDays: AbstractControl<boolean[]> | null) {
		if (workingDays && workingDays.value) {
			console.log('** set called on working days');
			this.ticketWorkdaysOn$.next(workingDays.value);

			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7368
			// this works if the working days component is interacted with to change the days but not triggered
			// when the duration is modified.  Need to figure out a way to trigger this when days change because of
			// duration change
			workingDays.valueChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe((values) => {
				if (values) {
					this.ticketWorkdaysOn$.next(values);
				}
			});
			// END PLACEHOLDER REPLACE AS PART OF TP-7368
		}
	}

	public ngOnInit(): void {
		combineLatest([this.ticketWorkdaysOn$, this.editTicket$, this.liveStart$])
			.pipe(
				map(([ticketWorkdaysOn, ticket, liveStart]) => {
					const roleId = ticket.view.liveRole?.$id || ticket.view.roleId;
					return WorkDayUtils.calculateWorkDays(this.calendarWorkdays, ticketWorkdaysOn, liveStart, roleId);
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((workdays: Array<{ date: Date; enabled: boolean; startOfWork: boolean }>) => {
				const chips = workdays.map(({ date, enabled }) => {
					const weekDay = DateUtils.getWeekDayMonthDay(date);
					// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7368
					const customStyle = ''; // set this to custom style when custom schedule values are entered for this date
					// END PLACEHOLDER REPLACE AS PART OF TP-7368
					return {
						id: date.toString(),
						label: `${weekDay.weekDay} ${weekDay.dayOfMonth}`,
						isOn: enabled,
						disabled: !enabled,
						styleClass: customStyle,
					};
				});
				this.scheduleChips$.next(chips);
			});

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7368
		// Send the changed array values "out" to the externally bound FormControl:
		// ticketsBooleanArrayValue$.subscribe((booleans) => {
		// 	this.onValueChange(booleans);
		// });
		// END PLACEHOLDER REPLACE AS PART OF TP-7368
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	public selectWorkDay(chip: DayChip) {
		this.onTouched();

		if (this.selectedChipId === chip.id) {
			this.selectedChipId = undefined;
		} else {
			this.selectedChipId = chip.id;
		}
		this.updateChipList();
	}

	public updateChipList() {
		const chips = this.scheduleChips$.getValue();
		chips.forEach((listChip) => {
			listChip.isSelected = listChip.id === this.selectedChipId;
		});
		this.scheduleChips$.next([...chips]);
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7368
		// trigger onValueChange when the values are updated
		// END PLACEHOLDER REPLACE AS PART OF TP-7368
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7368
	// implement resetAll and clear functions
	// implement writeValue
	public resetAll() {
		console.log(`clicked 'RESET ALL'`);
	}

	public clear() {
		console.log(`clicked 'Clear'`);
	}

	public writeValue(obj: any): void {
		// todo: send data back to form when changes
		// if (obj === null || obj === undefined) {
		// 	obj = [];
		// }
		// if (!Array.isArray(obj)) {
		// 	this.logger.warn("<workdays> component's FormControl value is not an array", obj);
		// 	return;
		// }
		// if (obj.some((item) => typeof item !== 'boolean')) {
		// 	this.logger.warn("<workdays> component's FormControl value is not an array of booleans", obj);
		// 	return;
		// }
		// // cast obj after validation
		// this.ticketWorkdayBoolean$.next(obj as boolean[]);
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-7368

	/**
	 * ControlValueAccessor function:
	 * This method is called by Angular to give this class a callback method, which is to be called
	 * when the (externally visible) value of this component should be updated.
	 */
	public registerOnChange(fn: (_: boolean[]) => void): void {
		this.onValueChange = fn;
	}

	/**
	 * ControlValueAccessor function:
	 * This is another method that registers a callback function.  This one needs to be called when this
	 * component is "touched".  We track that via the "(blur)" event handler in the HTML template, which
	 * calls the "onBlur()" method.  That in turn calls the callback method provided as a parameter here.
	 */
	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	/**
	 * ControlValueAccessor function: to disable the component
	 * @param isDisabled true to disable the component
	 */
	public setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
	}
}
